import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, Clock, Wallet, PhoneOff, AlertTriangle, Check, CheckCheck, Star, ShieldAlert, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

function fmt(sec) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function ChatRoom() {
  const { id } = useParams();
  const { user, subscribe, sendWs, wsConnected } = useAuth();
  const navigate = useNavigate();

  const [consultation, setConsultation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [balance, setBalance] = useState(null);
  const [spent, setSpent] = useState(0);
  const [otherTyping, setOtherTyping] = useState(false);
  const [summary, setSummary] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const bottomRef = useRef(null);
  const typingTimeout = useRef(null);

  const isExpert = consultation && user?.id === consultation.expert_id;
  const status = consultation?.status;

  const load = useCallback(async () => {
    try {
      const { data } = await api.getConsultation(id);
      setConsultation(data);
      setSpent(data.credits_spent || 0);
      setElapsed(data.duration_seconds || 0);
      const { data: msgs } = await api.getMessages(id);
      setMessages(msgs);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); navigate("/"); }
  }, [id, navigate]);

  useEffect(() => { load(); api.wallet().then(({ data }) => setBalance(Math.round(data.balance * 100) / 100)).catch(() => {}); }, [load]);

  useEffect(() => {
    return subscribe((msg) => {
      if (msg.consultation_id && msg.consultation_id !== id && msg.type !== "message") return;
      switch (msg.type) {
        case "message":
          if (msg.message.consultation_id !== id) return;
          setMessages((m) => m.some((x) => x.id === msg.message.id) ? m : [...m, msg.message]);
          if (msg.message.sender_id !== user.id) sendWs({ type: "read", consultation_id: id, message_id: msg.message.id });
          break;
        case "session_started":
          setConsultation((c) => c ? { ...c, status: "active", started_at: msg.started_at } : c);
          toast.success("La consultation a démarré. La facturation est active.");
          break;
        case "billing_tick":
          setBalance(msg.balance); setSpent(msg.credits_spent); setElapsed(msg.elapsed_seconds);
          break;
        case "low_balance":
          toast.warning("Solde bientôt épuisé — pensez à recharger pour poursuivre.");
          break;
        case "session_ended":
          setConsultation((c) => c ? { ...c, status: "completed" } : c);
          setSummary(msg);
          if (msg.reason === "credits_epuises") toast.warning("Consultation terminée : crédits épuisés.");
          else toast.info("Consultation terminée.");
          if (!isExpert) setReviewOpen(true);
          break;
        case "read_receipt":
          setMessages((m) => m.map((x) => x.id === msg.message_id ? { ...x, read: true } : x));
          break;
        case "typing":
          setOtherTyping(true);
          clearTimeout(typingTimeout.current);
          typingTimeout.current = setTimeout(() => setOtherTyping(false), 1500);
          break;
        case "consultation_refused":
          toast.error("L'expert a refusé la demande."); navigate("/tableau-de-bord"); break;
        case "consultation_cancelled":
          toast.info("La demande a été annulée."); navigate("/expert"); break;
        default: break;
      }
    });
  }, [subscribe, id, user, sendWs, isExpert, navigate]);

  // local ticking timer while active
  useEffect(() => {
    if (status !== "active") return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [status]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, otherTyping]);

  const send = () => {
    const t = text.trim();
    if (!t || status !== "active") return;
    sendWs({ type: "message", consultation_id: id, text: t });
    setText("");
  };

  const onType = (e) => {
    setText(e.target.value);
    sendWs({ type: "typing", consultation_id: id });
  };

  const accept = async () => { try { const { data } = await api.acceptConsultation(id); setConsultation(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const refuse = async () => { try { await api.refuseConsultation(id); navigate("/expert"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const endSession = async () => { try { await api.endConsultation(id); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const submitReview = async () => {
    try { await api.createReview({ consultation_id: id, rating, comment }); toast.success("Merci pour votre avis !"); setReviewOpen(false); navigate("/tableau-de-bord"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitDispute = async () => {
    try { await api.dispute({ consultation_id: id, reason: disputeReason || "Litige consultation" }); toast.success("Litige transmis à la modération."); setDisputeOpen(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!consultation) return <div className="py-24 text-center text-celeste-lilas/70">Chargement…</div>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
      <div className="glass flex flex-col overflow-hidden rounded-3xl border border-celeste-or/20" style={{ height: "78vh" }}>
        {/* header */}
        <div className="flex items-center justify-between gap-3 border-b border-celeste-or/15 bg-celeste-night/50 p-4">
          <div className="flex items-center gap-3">
            <img src={consultation.expert_photo} alt="" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <p className="font-serif text-celeste-ivoire">{isExpert ? consultation.consultant_name : consultation.expert_name}</p>
              <p className="flex items-center gap-1.5 text-xs text-celeste-lilas/70">
                <span className={`h-2 w-2 rounded-full ${wsConnected ? "bg-emerald-400" : "bg-red-400"}`} />
                {wsConnected ? "Connecté" : "Reconnexion…"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="flex items-center gap-1 font-mono text-lg text-celeste-or" data-testid="chat-timer"><Clock className="h-4 w-4" /> {fmt(elapsed)}</p>
              <p className="text-[11px] text-celeste-lilas/60">{consultation.price_per_min} cr/min · {Math.round(spent * 100) / 100} cr</p>
            </div>
            {!isExpert && (
              <div className="hidden text-right sm:block">
                <p className="flex items-center gap-1 text-sm text-celeste-lilas" data-testid="chat-balance"><Wallet className="h-4 w-4 text-celeste-or" /> {balance ?? "…"}</p>
                <p className="text-[11px] text-celeste-lilas/60">solde</p>
              </div>
            )}
          </div>
        </div>

        {/* body */}
        <div className="scroll-thin flex-1 space-y-3 overflow-y-auto p-4">
          {status === "pending" && (
            <div className="flex h-full flex-col items-center justify-center text-center" data-testid="pending-panel">
              {isExpert ? (
                <>
                  <p className="font-serif text-xl text-celeste-ivoire">Nouvelle demande de consultation</p>
                  <p className="mt-2 text-sm text-celeste-lilas/70">{consultation.consultant_name} souhaite échanger avec vous.</p>
                  <div className="mt-6 flex gap-3">
                    <Button data-testid="accept-consultation" onClick={accept} className="gold-btn font-semibold px-6">Accepter</Button>
                    <Button data-testid="refuse-consultation" onClick={refuse} variant="ghost" className="border border-red-400/40 text-red-200 hover:bg-red-500/10 px-6">Refuser</Button>
                  </div>
                  <p className="mt-4 text-xs text-celeste-lilas/50">La facturation ne démarre qu'après votre acceptation.</p>
                </>
              ) : (
                <>
                  <Loader2 className="h-10 w-10 animate-spin text-celeste-or" />
                  <p className="mt-4 font-serif text-xl text-celeste-ivoire">En attente d'acceptation…</p>
                  <p className="mt-2 text-sm text-celeste-lilas/70">{consultation.expert_name} va bientôt vous répondre. Aucun crédit n'est débité tant que la session n'a pas démarré.</p>
                  <Button data-testid="cancel-consultation" variant="ghost" className="mt-6 border border-celeste-violet/30 text-celeste-lilas" onClick={async () => { try { await api.cancelConsultation(id); navigate("/experts"); } catch {} }}>Annuler la demande</Button>
                </>
              )}
            </div>
          )}

          {["refused", "cancelled"].includes(status) && (
            <div className="flex h-full items-center justify-center text-celeste-lilas/70">Cette consultation n'a pas eu lieu.</div>
          )}

          {(status === "active" || status === "completed") && messages.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm ${mine ? "gold-btn rounded-br-sm" : "bg-celeste-violet/25 text-celeste-ivoire rounded-bl-sm border border-celeste-violet/30"}`} data-testid="chat-message">
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                  <div className={`mt-1 flex items-center gap-1 text-[10px] ${mine ? "text-celeste-prune/70" : "text-celeste-lilas/50"}`}>
                    {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                    {mine && (m.read ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />)}
                  </div>
                  {m.filtered && <p className="mt-1 text-[10px] text-red-300">Coordonnées masquées par sécurité.</p>}
                </div>
              </div>
            );
          })}
          {otherTyping && status === "active" && <p className="text-xs italic text-celeste-lilas/50">En train d'écrire…</p>}

          {status === "completed" && summary && (
            <div className="mx-auto mt-4 max-w-sm rounded-2xl border border-celeste-or/25 bg-celeste-night/60 p-4 text-center" data-testid="session-summary">
              <p className="font-serif text-celeste-or">Récapitulatif</p>
              <p className="mt-2 text-sm text-celeste-lilas">Durée : {fmt(summary.duration_seconds || elapsed)} · {Math.round((summary.credits_spent ?? spent) * 100) / 100} crédits</p>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* footer */}
        {status === "active" && (
          <div className="border-t border-celeste-or/15 bg-celeste-night/50 p-3">
            <div className="flex items-center gap-2">
              <Button data-testid="end-session-btn" variant="ghost" size="icon" onClick={endSession} className="text-red-300 hover:bg-red-500/10" title="Terminer">
                <PhoneOff className="h-5 w-5" />
              </Button>
              <Button data-testid="dispute-btn" variant="ghost" size="icon" onClick={() => setDisputeOpen(true)} className="text-celeste-lilas hover:bg-celeste-violet/15" title="Signaler un litige">
                <ShieldAlert className="h-5 w-5" />
              </Button>
              <Input data-testid="chat-input" value={text} onChange={onType} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Écrivez votre message…" className="flex-1 bg-celeste-night/60 border-celeste-violet/30 text-celeste-ivoire" />
              <Button data-testid="send-message-btn" onClick={send} className="gold-btn"><Send className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
        {status === "completed" && (
          <div className="border-t border-celeste-or/15 bg-celeste-night/50 p-3 text-center">
            <Button className="gold-btn font-semibold" onClick={() => navigate(isExpert ? "/expert" : "/tableau-de-bord")}>Retour à mon espace</Button>
          </div>
        )}
      </div>

      {/* review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="glass-strong text-celeste-ivoire border-celeste-or/20" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-serif text-celeste-or">Votre avis (consultation vérifiée)</DialogTitle></DialogHeader>
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}>
                <Star className={`h-8 w-8 ${n <= rating ? "fill-celeste-or text-celeste-or" : "text-celeste-lilas/30"}`} />
              </button>
            ))}
          </div>
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Partagez votre ressenti…" className="bg-celeste-night/60 border-celeste-violet/30" />
          <DialogFooter>
            <Button variant="ghost" className="border border-celeste-violet/30 text-celeste-lilas" onClick={() => { setReviewOpen(false); navigate("/tableau-de-bord"); }}>Plus tard</Button>
            <Button data-testid="submit-review" onClick={submitReview} className="gold-btn font-semibold">Publier l'avis</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* dispute dialog */}
      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent className="glass-strong text-celeste-ivoire border-celeste-or/20" data-testid="dispute-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-serif text-celeste-or"><AlertTriangle className="h-4 w-4" /> Signaler un litige</DialogTitle></DialogHeader>
          <Textarea data-testid="dispute-reason" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="Expliquez le problème rencontré…" className="bg-celeste-night/60 border-celeste-violet/30" />
          <DialogFooter>
            <Button data-testid="submit-dispute" onClick={submitDispute} className="gold-btn font-semibold">Transmettre à la modération</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
